import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { formattedError } from '@helpers/functions';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../_services/api-service';
import { PortalService } from '../../_services/portal.service';
import { TokenService } from '../../_services/token.service';
import { DadosColaborador, DataColab, User } from '../../models';
import { AlertService } from '../../shared/alert/alert.service';

import { DadosLogin, PortaisRotas } from '../interfaces/dados-login.interface';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private jwtHelperService = new JwtHelperService();
    private token: any;
    private permissions: Array<keyof PortaisRotas | string> | undefined;
    private url: string = environment.apiUrl;

    private userauthenticated: boolean;
    get isLogged() {
        return this.userauthenticated;
    }

    private _rotas: PortaisRotas = {
        Colaborador: '/',
        Administrador: '/administrador',
        Coordenador: '/coordenador',
        Gestor: '/gestor',
    };

    private readonly dadosLoginSubject = new BehaviorSubject<
        DadosLogin | undefined
    >(undefined);
    readonly dadosLogin$ = this.dadosLoginSubject.asObservable();

    private readonly loadingSubject = new BehaviorSubject<boolean>(true);
    readonly loading = this.loadingSubject.asObservable();

    private cacheDadosLogin: Observable<DadosLogin | Error>;

    current_user: DadosColaborador | undefined;
    dados_login: DadosLogin | undefined;

    get rotas() {
        return this._rotas;
    }

    constructor(
        private alertService: AlertService,
        private apiService: ApiService,
        private http: HttpClient,
        private portalService: PortalService,
        private router: Router,
        private toastrService: ToastrService,
        private tokenService: TokenService
    ) {
        const helper = new JwtHelperService();
        this.token = this.tokenService.token;

        try {
            this.userauthenticated = !helper.isTokenExpired(this.token);
        } catch (err) {
            console.log(err);
        }
    }

    check() {
        return this.http.post(`${this.url}check`, {});
    }

    //alterar senha
    alterarSenha(cpf: string) {
        const formData = new FormData();
        formData.append('cpf', cpf);

        return this.http.post(
            `${this.url}colaboradores/esqueci_senha/`,
            formData
        );
    }

    login(user: User) {
        const helper = new JwtHelperService();
        return this.http.post<any>(`${this.url}api-token-auth/`, user).pipe(
            tap(res => {
                this.token = res['token'];
                this.userauthenticated = !!this.token;
                if (!this.token) return;

                const expiration_time = helper
                    .getTokenExpirationDate(this.token)
                    .getTime();

                this.tokenService.store(this.token, expiration_time);
            })
        );
    }

    armazenaToken = (res: { token: string | null }) => {
        this.token = res.token;
        this.userauthenticated = !!this.token;
        if (!this.token) return;

        const tokenExpirationDate =
            this.jwtHelperService.getTokenExpirationDate(this.token);
        if (!tokenExpirationDate) {
            this.alertService.error(
                '[JWT Token] Não foi possível decodificar o token'
            );
        } else {
            this.tokenService.store(this.token, tokenExpirationDate.getTime());
        }
    };

    dadosLogin(reload = false): Observable<DadosLogin | Error> {
        if (reload) {
            this.cacheDadosLogin = null; // reseta o cache
        }

        if (!this.cacheDadosLogin) {
            this.cacheDadosLogin = new Observable<DadosLogin | Error>(
                subscriber => {
                    // executa a requisição para o backend uma única vez
                    // e repete o resultado para todos os componentes utilizando
                    // o operador shareReplay
                    this.http
                        .get<DadosLogin>(`${this.url}colaboradores/dadoslogin/`)
                        .pipe(
                            map(
                                this
                                    .adicionaOLinkDoPortalDeCadaGrupoQueOUsuarioTemAcesso
                            ),
                            tap(
                                res => {
                                    this.dados_login = res;
                                    this.permissions = res.grupos.map(
                                        grupo =>
                                            grupo.id_grupo_usuario.grupo_usuario
                                    );
                                    this.portalService.setPortais(res.grupos);
                                    this.loadingSubject.next(false);
                                },
                                err => this.alertService.error(err)
                            ),
                            catchError(err => {
                                this.toastrService.error(
                                    'Erro na autenticação'
                                );
                                this.alertService.error(
                                    formattedError(err),
                                    true
                                );
                                this.logout();
                                return of(new Error('Erro nos dados de login'));
                            })
                        )
                        .subscribe(res => {
                            subscriber.next(res);
                            subscriber.complete();
                        });
                }
            ).pipe(shareReplay());
        }
        return this.cacheDadosLogin;
    }

    private adicionaOLinkDoPortalDeCadaGrupoQueOUsuarioTemAcesso = (
        res: DadosLogin
    ): DadosLogin => {
        const gruposComLinksDePortal: DadosLogin['grupos'] = res.grupos.map(
            el => ({
                id_grupo_usuario: {
                    ...el.id_grupo_usuario,
                    link: this._rotas[el.id_grupo_usuario.grupo_usuario], // Link do portal
                },
            })
        );
        return { ...res, grupos: gruposComLinksDePortal };
    };

    async refreshToken(): Promise<boolean> {
        try {
            await this.refresh_token().toPromise();
            return Promise.resolve(true);
        } catch (err) {
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(false);
        }
    }

    refresh_token() {
        return this.http
            .post<any>(`${this.url}refresh-token/`, { token: this.token })
            .pipe(tap(this.armazenaToken));
    }

    clearVariables() {
        this.dados_login = undefined;
        this.current_user = undefined;
        this.permissions = undefined;
        this.apiService.clearCurrent();
    }

    async logout() {
        this.http
            .post<any>(`${this.url}api-token-auth/logout/`, {
                token: this.token,
            })
            .subscribe();

        this.dadosLoginSubject.next(undefined);
        this.userauthenticated = false;
        this.tokenService.clear();
        this.clearVariables();
        await this.router.navigate(['/login'], { replaceUrl: true });
    }
    /**
     * function to obtain the data of actual user
     */
    getCurrentColaborador() {
        if (this.dados_login) {
            return of(this.dados_login.dados);
        } else {
            return this.http
                .get<DataColab>(this.url + 'colaboradoresdadosbasicos/')
                .pipe(map(res => res[0]));
        }
    }

    updatePassword(user) {
        return this.http
            .put<any>(
                this.url +
                    `usercrud/${this.current_user.dados_basicos.id_user}/`,
                user
            )
            .pipe(
                tap(res =>
                    this.toastrService.success(
                        'Senha Atualizada',
                        '[AuthService]'
                    )
                )
            );
    }

    getPermissions() {
        return this.permissions;
    }

    checkPermissionPortal(absolutePath: string) {
        const basePathParsed = absolutePath.startsWith('/')
            ? `/${absolutePath.split('/')[1]}`
            : `/${absolutePath.split('/')[0]}`;
        return this.basePathPortais?.includes(basePathParsed) || false;
    }

    get basePathPortais() {
        return this.getPermissions()?.map(
            permission => this._rotas[permission]
        );
    }

    redirect(goTo?: string, callback?: () => void) {
        const finishNotifier = new Subject<void>();

        const redirecionaFn = (isLoading: boolean) => {
            if (isLoading) return;
            finishNotifier.next();

            if (callback) callback();

            const acessoPermitido = goTo && this.checkPermissionPortal(goTo);
            const naoPossuiPermissao = this.getPermissions()?.length === 0;
            const rotaAleatoriaQueOUsuarioTemAcesso =
                this.basePathPortais && this.basePathPortais.length !== 0
                    ? this.basePathPortais.find(el => el !== undefined)
                    : '/';

            if (acessoPermitido) this.router.navigateByUrl(goTo);
            else if (naoPossuiPermissao) this.router.navigateByUrl('/404');
            else this.router.navigateByUrl(rotaAleatoriaQueOUsuarioTemAcesso);
        };

        this.loading.pipe(takeUntil(finishNotifier)).subscribe(redirecionaFn);
    }
}
